<template>
    <div class="flex-grow-1 position-relative">
        <!-- Toolbar flottante : bouton + slot à droite -->
        <div class="map-toolbar">
            <button class="btn btn-light shadow-sm map-settings-btn" @click="open = !open"
                :title="hasActivePanelFilters ? (panelFiltersLabel || 'Des filtres sont actifs') : 'Paramètres'">
                <span class="map-settings-btn-label">⚙️ Paramètres</span>
                <span v-if="hasActivePanelFilters" class="map-settings-subtext">
                    {{ panelFiltersLabel }}
                </span>
            </button>
            <div class="toolbar-right">
                <!-- Contenu libre (ex: SearchDisplay) -->
                <slot name="toolbar-right" />
            </div>
        </div>

        <slot name="map-overlays" :map-instance="leafletMapInstance" :map-getter="getLeafletMap" />

        <!-- Boutons flottants pour les panneaux droits (variables) -->
        <div v-if="rightPanels && rightPanels.length" class="right-panel-buttons">
            <button v-for="panel in rightPanels" :key="panel.id" type="button" class="btn btn-light shadow-sm mb-2"
                :class="{ 'active': panel.id === activeRightPanel }" :title="panel.title || ''"
                @click="openRightPanel(panel.id)">
                <span v-if="panel.icon">{{ panel.icon }}</span>
            </button>
        </div>

        <!-- Panneau latéral commun (gauche) -->
        <MapSidePanel v-model="open" :tile-providers="tileProviders" :center="viewCenter" :zoom="viewZoom"
            @select-base-layer="selectBaseLayer">
            <!-- Header du panel (optionnel) : recherche d'adresse -->
            <template v-if="enableAddressSearch" #header>
                <PanelAddressSearch @select="applyAddressResult" />
            </template>
            <!-- Sections personnalisées injectées par chaque page -->
            <template #sections>
                <PanelSectionCorridoring v-if="enableCorridorings" :corridorings="corridorings"
                    :selected-id="effectiveSelectedCorridoringId" :loading="corridoringsLoading"
                    @select="onSelectCorridoring" @refresh="loadCorridorings" />
                <slot name="panel-sections" />
            </template>
        </MapSidePanel>

        <!-- Carte Leaflet commune -->
        <l-map :zoom="viewZoom" :center="viewCenter" :bounds="viewBounds" ref="leafletMap"
            :options="{ attributionControl: false, zoomControl: false }" :max-bounds="maxBounds" :maxZoom="maxZoom"
            :minZoom="minZoom" @click="$emit('leaflet-click', $event.latlng)" @update:zoom="onMapZoom"
            @update:center="onMapCenter">
            <l-tile-layer v-for="tp in tileProviders" :key="tp.name" :name="tp.name" :visible="tp.visible" :url="tp.url"
                :maxZoom="maxZoom" layer-type="base" />
            <l-control-scale position="bottomright" :imperial="false" :metric="true" />
            <l-control-zoom position="bottomright" />

            <!-- Calques spécifiques de chaque page -->
            <slot />
            <CorridoringDisplay v-if="enableCorridorings && showCorridoringOverlay && effectiveSelectedCorridoringId"
                :corridoringId="effectiveSelectedCorridoringId" />
        </l-map>

        <!-- Panneau latéral droit : contenu 100% piloté par la carte -->
        <MapSidePanelRight v-model="rightOpen">
            <slot name="right-panel" :active-panel="activeRightPanel" />
        </MapSidePanelRight>
    </div>
</template>

<script>
import "leaflet/dist/leaflet.css";
import { LMap, LTileLayer, LControlScale, LControlZoom } from "@vue-leaflet/vue-leaflet";
import MapSidePanel from "./Components/MapSidePanel.vue";
import MapSidePanelRight from "./Components/MapSidePanelRight.vue";
import PanelAddressSearch from "./Components/sidePanel/PanelAddressSearch.vue";
import PanelSectionCorridoring from "./Components/sidePanel/PanelSectionCorridoring.vue";
import CorridoringDisplay from "./Components/CorridoringDisplay.vue";
import CorridoringService from "@/services/corridoring.service.js";


export default {
    name: "BaseMapShell",
    components: {
        LMap,
        LTileLayer,
        LControlScale,
        LControlZoom,
        MapSidePanel,
        MapSidePanelRight,
        PanelAddressSearch,
        PanelSectionCorridoring,
        CorridoringDisplay
    },
    props: {
        /** État d’ouverture du panneau (v-model:panelOpen) */
        panelOpen: { type: Boolean, default: false },
        rightPanelOpen: { type: Boolean, default: false },

        /** Vue initiale de la carte */
        center: { type: Array, required: true },
        zoom: { type: Number, required: true },

        /** Contraintes/limites de carte */
        bounds: { type: Array, default: () => [] },
        maxBounds: { type: Array, default: () => [] },
        maxZoom: { type: Number, default: 19 },
        minZoom: { type: Number, default: 3 },

        /** Déclaration des panneaux droits disponibles (gérés par la carte) */
        rightPanels: { type: Array, default: () => [] },
        /** Panneau droit actif (v-model:activeRightPanelId) */
        activeRightPanelId: { type: [String, Number, null], default: null },

        /** Affiche la recherche d'adresse dans le panel gauche */
        enableAddressSearch: { type: Boolean, default: true },

        /** Active la feature corridoring plug and play */
        enableCorridorings: { type: Boolean, default: false },
        showCorridoringOverlay: { type: Boolean, default: true },
        selectedCorridoringId: { type: [String, Number, null], default: null },

        /** Zoom appliqué quand Nominatim ne renvoie pas de bbox */
        addressSearchZoom: { type: Number, default: 16 },

        /** Indicateur générique d'état des filtres du panneau gauche */
        hasActivePanelFilters: { type: Boolean, default: false },
        panelFiltersLabel: { type: String, default: "" },
    },
    emits: [
        "update:panelOpen",
        "update:rightPanelOpen",
        "update:activeRightPanelId",
        "update:selectedCorridoringId",
        "leaflet-click",
        "leaflet-zoom",
        "leaflet-center"
    ],
    data() {
        return {
            tileProviders: [
                {
                    name: "Classique",
                    visible: true,
                    url: "https://api.maptiler.com/maps/bright-v2/{z}/{x}/{y}.png?key=EevE8zHrA8OKNsj637Ms",
                    preview: "/img/carte-classique.jpg",
                },
                {
                    name: "Satellite",
                    visible: false,
                    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
                    preview: "/img/carte-satellite.jpg",
                },
            ],
            // ✅ Vue interne : permet un comportement identique partout (sans code dans les pages)
            viewCenter: this.center,
            viewZoom: this.zoom,
            viewBounds: this.bounds,
            corridorings: [],
            corridoringsLoading: false,
            internalSelectedCorridoringId: this.selectedCorridoringId,
        };
    },
    mounted() {
        if (this.enableCorridorings) {
            this.loadCorridorings();
        }
    },
    watch: {
        // Si la page impose une nouvelle vue (ex: mode follow), on resynchronise
        center: { deep: true, handler(v) { this.viewCenter = v; } },
        zoom(v) { this.viewZoom = v; },
        bounds: { deep: true, handler(v) { this.viewBounds = v; } },
        selectedCorridoringId(v) {
            this.internalSelectedCorridoringId = v;
        },
        enableCorridorings(v) {
            if (v && !this.corridorings.length) {
                this.loadCorridorings();
            }
        },
    },
    computed: {
        open: {
            get() { return this.panelOpen; },
            set(v) { this.$emit("update:panelOpen", v); }
        },
        rightOpen: {
            get() { return this.rightPanelOpen; },
            set(v) { this.$emit("update:rightPanelOpen", v); }
        },
        activeRightPanel: {
            get() { return this.activeRightPanelId; },
            set(v) { this.$emit("update:activeRightPanelId", v); }
        },
        effectiveSelectedCorridoringId() {
            return this.internalSelectedCorridoringId;
        },
        leafletMapInstance() {
            return this.$refs?.leafletMap?.leafletObject || null;
        },
    },
    methods: {
        getLeafletMap() {
            return this.$refs?.leafletMap?.leafletObject || null;
        },
        normalizeCenter(center) {
            if (Array.isArray(center)) return center;
            if (
                center
                && Number.isFinite(center.lat)
                && Number.isFinite(center.lng)
            ) {
                return [center.lat, center.lng];
            }
            return this.viewCenter;
        },
        selectBaseLayer(name) {
            this.tileProviders = this.tileProviders.map(tp => ({ ...tp, visible: tp.name === name }));
        },
        openRightPanel(panelId) {
            this.activeRightPanel = panelId;
            this.rightOpen = true;
        },
        // ✅ Interactions carte : on met à jour la vue interne  on émet pour compat (tes pages actuelles)
        onMapZoom(newZoom) {
            this.viewZoom = newZoom;
            this.$emit("leaflet-zoom", newZoom);
        },
        onMapCenter(newCenter) {
            const normalizedCenter = this.normalizeCenter(newCenter);
            this.viewCenter = normalizedCenter;
            this.$emit("leaflet-center", normalizedCenter);
        },

        // ✅ Recherche adresse : comportement identique partout
        applyAddressResult({ lat, lng, bbox }) {
            const hasBbox =
                bbox &&
                Number.isFinite(bbox.south) &&
                Number.isFinite(bbox.north) &&
                Number.isFinite(bbox.west) &&
                Number.isFinite(bbox.east);

            if (hasBbox) {
                this.viewBounds = [
                    [bbox.south, bbox.west],
                    [bbox.north, bbox.east],
                ];
            } else {
                this.viewBounds = [];
                this.viewCenter = [lat, lng];
                this.viewZoom = this.addressSearchZoom;
            }

            // compat
            this.$emit("leaflet-center", [lat, lng]);
            if (!hasBbox) this.$emit("leaflet-zoom", this.viewZoom);
        },
        async loadCorridorings(search = "") {
            if (!this.enableCorridorings) return;

            try {
                this.corridoringsLoading = true;

                const response = await CorridoringService.getClientCorridorings(search);

                const rows = Array.isArray(response?.data?.rows)
                    ? response.data.rows
                    : Array.isArray(response?.data)
                        ? response.data
                        : [];

                this.corridorings = rows.map(c => ({
                    id: c.id,
                    name: c.name,
                }));
            } catch (e) {
                console.error("Erreur lors du chargement des corridorings", e);
                this.corridorings = [];
            } finally {
                this.corridoringsLoading = false;
            }
        },
        onSelectCorridoring(corridoringId) {
            const value = corridoringId ? Number(corridoringId) : null;
            this.internalSelectedCorridoringId = value;
            this.$emit("update:selectedCorridoringId", value);
        },

        /**
         * ✅ API publique (optionnelle) : depuis une page, piloter la vue du shell.
         * this.$refs.mapShell.setView({ center:[lat,lng], zoom: 16 }) ou { bounds:[[...],[...]] }
         */
        setView({ center, zoom, bounds } = {}) {
            if (Array.isArray(bounds) && bounds.length) {
                this.viewBounds = bounds;
                return;
            }
            let touched = false;
            if (Array.isArray(center) && center.length === 2) {
                this.viewCenter = center;
                this.$emit("leaflet-center", center);
                touched = true;
            }
            if (Number.isFinite(zoom)) {
                this.viewZoom = zoom;
                this.$emit("leaflet-zoom", zoom);
                touched = true;
            }
            if (touched) this.viewBounds = [];
        },
    }
};
</script>

<style scoped>
/* Position des contrôles Leaflet (zoom + échelle) : fixés au viewport */
:deep(.leaflet-bottom.leaflet-right) {
    position: fixed;
    /* 🔴 clé : ne dépend plus de la carte, mais de la fenêtre */
    bottom: 0.3rem;
    /* marge par rapport au bas de l'écran */
    right: 0.75rem;
    /* marge par rapport au bord droit */
    z-index: 1800;
    /* au-dessus de la carte mais sous le header (qui est à 2001) */
}

/* Barre verticale de boutons de panneaux droits */
.right-panel-buttons {
    position: absolute;
    top: 30%;
    right: 1rem;
    z-index: 2000;
    display: flex;
    flex-direction: column;
    align-items: stretch;
}

.right-panel-buttons .btn.active {
    border-width: 2px;
}

.right-panel-buttons .btn {
    background-color: #fff !important;
    border: 1px solid rgba(0, 0, 0, 0.15) !important;
    border-radius: 0.375rem;
    /* match btn-light radius */
}

@media (min-width: 769px) {
    .right-panel-buttons {
        right: 1.5rem;
    }
}

@media (min-width: 769px) {
    :deep(.leaflet-bottom.leaflet-right) {
        bottom: 0.5rem;
    }
}

/* ===== Toolbar flottante (bouton + zone extensible à droite) ===== */
.map-toolbar {
    position: absolute;
    top: 1rem;
    left: 1rem;
    right: 1rem;
    /* pour le mode mobile quand le slot s’étire */
    z-index: 2000;
    display: flex;
    align-items: center;
    /* aligne verticalement bouton & contenu */
    gap: .75rem;
    /* espace entre bouton et slot droit */
}

/* Le contenu du slot à droite (ex: SearchDisplay) */
.toolbar-right {
    flex: 1;
    min-width: 0;
    /* permet au flex de réduire correctement */
}

.toolbar-right>* {
    width: 100%;
    /* le composant prend toute la largeur allouée */
}

/* Desktop : largeur max confortable pour la zone de droite */
@media (min-width: 769px) {
    .toolbar-right {
        max-width: 360px;
    }
}

.map-settings-btn {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    white-space: normal;
    text-align: left;
    line-height: 1.1;
}

.map-settings-btn-label {
    display: inline-flex;
    align-items: center;
    gap: .4rem;
    white-space: nowrap;
}

.map-settings-subtext {
    margin-top: .35rem;
    color: #0d6efd;
    font-size: .78rem;
    font-weight: 700;
    line-height: 1;
    opacity: 0.95;
}

.map-settings-btn:hover .map-settings-subtext {
    color: #0b5ed7;
    opacity: 1;
}
</style>
